import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import { authProblem } from '@/entities/auth';
import type { Session, SessionTenant } from '@/shared/api';

import { authCopy } from './copy';
import { logout, switchTenant } from './session-lifecycle';

/** Uitloggen en terug naar het inlogscherm. */
export function useLogout(): () => Promise<void> {
    const navigate = useNavigate();

    return useCallback(async () => {
        await logout();
        void navigate(paths.login, { replace: true });
    }, [navigate]);
}

export interface TenantSwitchInput {
    tenant: SessionTenant;
    /** Maak dit de omgeving die na de volgende keer inloggen opent. */
    remember?: boolean;
}

/**
 * Wisselen van omgeving via `POST /auth/switch-tenant`.
 *
 * Het nieuwe token vervangt de sessie; de schil gooit daarna alles van de vorige
 * omgeving uit de cache (`SessionCacheGuard`). Mislukt het, dan blijft alles
 * zoals het was en toont het scherm `tenantSwitchError(error)`.
 */
export function useTenantSwitch(): UseMutationResult<Session, Error, TenantSwitchInput> {
    return useMutation({
        mutationFn: ({ tenant, remember }: TenantSwitchInput) =>
            switchTenant(tenant.uuid, remember ? { remember } : {}),
    });
}

/** De melding bij een mislukte wissel. */
export function tenantSwitchError(error: unknown): string {
    switch (authProblem(error)) {
        case 'offline':
            return authCopy.tenantOffline;
        case 'tenant-access-denied':
            return authCopy.tenantDenied;
        default:
            return authCopy.tenantFailed;
    }
}
